/**
 * In-window panes for the Fias AI assistant: History (the shared
 * Make-a-Request thread store), Scheduled tasks (list / pause / resume /
 * cancel over /v1/schedules), and Options (temporary chat, admin verbose,
 * and the full memory manager). Each pane has an X in its header that
 * returns to chat — the bottom "Back to chat" link is easy to miss.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BellRing,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pause,
  Play,
  Repeat,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { AiMemoryCard } from '@/auth/components/AiMemoryCard';
import { useGlobalFiasAI } from './hooks/GlobalFiasAIContext';

const FIAS_AI_ARCHE_ID = 'arche_make_a_request';

// ---------------------------------------------------------------------------
// Shared pane header — title + an always-visible X back to chat so users
// never get stranded in a sub-panel.
// ---------------------------------------------------------------------------

function PaneHeader({
  title,
  icon: Icon,
  onClose,
}: {
  title: string;
  icon?: typeof CalendarClock;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-3 py-2">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {title}
      </p>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 rounded-lg"
        onClick={onClose}
        title="Back to chat"
        aria-label="Close and return to chat"
        data-testid="fias-ai-pane-close"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export function FiasAIHistoryPane({
  onSelect,
  onClose,
}: {
  onSelect: (conversationId: string) => void;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: [...queryKeys.fiasAI.all, 'conversations'],
    queryFn: () => api.listConversations({ archeId: FIAS_AI_ARCHE_ID, limit: 30 }),
  });
  const conversations = data?.conversations ?? [];
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="fias-ai-history-pane">
      <PaneHeader title="History" onClose={onClose} />
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {isLoading && <Loader2 className="mx-auto mt-6 h-4 w-4 animate-spin" />}
        {!isLoading && conversations.length === 0 && (
          <p className="pt-8 text-center text-sm text-muted-foreground">No conversations yet.</p>
        )}
        {conversations.map((c) => (
          <button
            key={c.conversationId}
            type="button"
            onClick={() => onSelect(c.conversationId)}
            className="block w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-muted/60"
          >
            <span className="block truncate font-medium">{c.title || 'Untitled'}</span>
            <span className="block text-xs text-muted-foreground">
              {new Date(c.updatedAt ?? c.createdAt).toLocaleString()}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheduled tasks
// ---------------------------------------------------------------------------

const ACTIVE_STATUSES = 'active,paused';
const TERMINAL_STATUSES = 'completed,failed,cancelled';
// A scheduled run fires server-side (worker → /scheduled-run) with no client
// mutation and no real-time push, so poll the visible tab while the pane is
// open so a run's effects (a one-off completing, fireCount/next-run advancing)
// show up without the user reopening the panel. Pauses when unfocused (React
// Query default) and stops on unmount (pane closed).
const SCHEDULE_POLL_MS = 10_000;

type ScheduleItem = Awaited<ReturnType<typeof api.listSchedules>>['actions'][number];

function statusBadgeClass(status: string): string {
  if (status === 'active') return 'bg-green-500/10 text-green-600';
  if (status === 'paused') return 'bg-amber-500/10 text-amber-600';
  if (status === 'failed') return 'bg-red-500/10 text-red-600';
  return 'bg-muted text-muted-foreground'; // completed, cancelled
}

/** Per-task run history — its own loading/error/empty states (narrowed DTO). */
function ScheduleExecutions({ id }: { id: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.fiasAI.scheduleExecutions(id),
    queryFn: () => api.listScheduleExecutions(id),
  });
  if (isLoading) return <Loader2 className="mx-auto my-2 h-3.5 w-3.5 animate-spin" />;
  if (isError)
    return <p className="py-1 text-center text-xs text-destructive">Couldn’t load run history.</p>;
  const runs = data?.executions ?? [];
  if (runs.length === 0)
    return <p className="py-1 text-center text-xs text-muted-foreground">No runs recorded.</p>;
  return (
    <ul className="mt-1 space-y-0.5 border-t border-border/60 pt-1">
      {runs.map((r) => (
        <li key={r.id} className="flex items-center justify-between gap-2 text-[11px]">
          <span className="text-muted-foreground">{new Date(r.firedAt).toLocaleString()}</span>
          <span
            className={`rounded px-1 py-0.5 uppercase tracking-wide ${statusBadgeClass(r.status)}`}
            title={r.error ?? undefined}
          >
            {r.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
      }`}
    >
      {children}
    </button>
  );
}

function ScheduleCard({
  a,
  tab,
  justCompleted,
  expanded,
  onToggleExpand,
  onPause,
  onResume,
  onCancel,
}: {
  a: ScheduleItem;
  tab: 'active' | 'history';
  justCompleted: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className={`mb-1.5 rounded-lg border px-2.5 py-2 text-sm ${
        justCompleted ? 'border-primary/40 bg-primary/5' : 'border-border'
      }`}
    >
      <div className="flex items-center gap-2">
        {justCompleted && (
          <span
            className="flex flex-shrink-0 text-primary"
            title="Finished since you last viewed History"
            aria-label="Finished since you last viewed History"
            data-testid="fias-ai-schedule-completed-alert"
          >
            <BellRing className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        )}
        <span className="min-w-0 flex-1 truncate font-medium">{a.name}</span>
        <span
          className="flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
          title={a.scheduleType === 'one_shot' ? 'Runs once' : 'Repeats'}
        >
          {a.scheduleType === 'one_shot' ? (
            'once'
          ) : (
            <>
              <Repeat className="h-2.5 w-2.5" /> recurring
            </>
          )}
        </span>
        <span
          className={`rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${statusBadgeClass(a.status)}`}
        >
          {a.status}
        </span>
      </div>
      {a.authorizedActions.length > 0 && (
        <div
          className="mt-1 flex items-center gap-1 text-[11px] text-amber-600"
          title={`This task can: ${a.authorizedActions.map((x) => x.label).join(', ')}`}
        >
          <Zap className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">
            Takes actions: {a.authorizedActions.map((x) => x.label).join(', ')}
          </span>
        </div>
      )}
      <div className="mt-0.5 text-xs text-muted-foreground">
        {a.description}
        {tab === 'active' && a.nextFireAt
          ? ` · next ${new Date(a.nextFireAt).toLocaleString()}`
          : ''}
        {tab === 'history' && a.lastFiredAt
          ? ` · last ran ${new Date(a.lastFiredAt).toLocaleString()}`
          : ''}
        {` · runs ${a.fireCount}${a.maxFires ? `/${a.maxFires}` : ''}`}
      </div>
      {tab === 'history' && a.lastError && (
        <div className="mt-0.5 truncate text-xs text-destructive" title={a.lastError}>
          {a.lastError}
        </div>
      )}
      <div className="mt-1 flex gap-1">
        {tab === 'active' && a.status === 'active' && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onPause}>
            <Pause className="mr-1 h-3 w-3" /> Pause
          </Button>
        )}
        {tab === 'active' && a.status === 'paused' && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onResume}>
            <Play className="mr-1 h-3 w-3" /> Resume
          </Button>
        )}
        {tab === 'active' && (a.status === 'active' || a.status === 'paused') && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-destructive"
            onClick={onCancel}
          >
            <Trash2 className="mr-1 h-3 w-3" /> Cancel
          </Button>
        )}
        {tab === 'history' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onToggleExpand}
            data-testid="fias-ai-schedule-runs-toggle"
          >
            {expanded ? (
              <ChevronDown className="mr-1 h-3 w-3" />
            ) : (
              <ChevronRight className="mr-1 h-3 w-3" />
            )}
            Runs
          </Button>
        )}
      </div>
      {tab === 'history' && expanded && <ScheduleExecutions id={a.id} />}
    </div>
  );
}

export function FiasAISchedulesPane({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const { scheduledAlertCount, markScheduledHistorySeen } = useGlobalFiasAI();
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // The "seen" watermark frozen for THIS History view: rows that finished after
  // it get a per-row alert icon so the user can tell WHICH task the badge was
  // about. Null until History is opened (no icons on the Active tab).
  const [alertSince, setAlertSince] = useState<string | null>(null);

  // Opening History counts as "seen" — clears the whole alert cascade (Fias AI
  // header button → schedules toggle → History tab). markScheduledHistorySeen is
  // a stable callback, so this runs only on a tab change, not every render. It
  // returns the PRIOR watermark, which we freeze to flag the just-finished rows.
  useEffect(() => {
    if (tab === 'history') setAlertSince(markScheduledHistorySeen());
  }, [tab, markScheduledHistorySeen]);

  const activeQuery = useQuery({
    queryKey: queryKeys.fiasAI.schedules('active'),
    queryFn: () =>
      api.listSchedules({ archeId: FIAS_AI_ARCHE_ID, status: ACTIVE_STATUSES, limit: 50 }),
    refetchInterval: tab === 'active' ? SCHEDULE_POLL_MS : false,
  });
  const historyQuery = useQuery({
    queryKey: queryKeys.fiasAI.schedules('history'),
    queryFn: () =>
      api.listSchedules({ archeId: FIAS_AI_ARCHE_ID, status: TERMINAL_STATUSES, limit: 50 }),
    enabled: tab === 'history',
    refetchInterval: tab === 'history' ? SCHEDULE_POLL_MS : false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.schedules('active') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiasAI.schedules('history') });
  };
  const pause = useMutation({
    mutationFn: (id: string) => api.pauseSchedule(id),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: (id: string) => api.resumeSchedule(id),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelSchedule(id),
    onSuccess: invalidate,
  });

  const q = tab === 'active' ? activeQuery : historyQuery;
  const items = q.data?.actions ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="fias-ai-schedules-pane">
      <PaneHeader title="Scheduled AI tasks" icon={CalendarClock} onClose={onClose} />
      <div className="flex flex-shrink-0 gap-1 border-b border-border px-3 py-1.5">
        <TabButton active={tab === 'active'} onClick={() => setTab('active')}>
          Active
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
          <span className="inline-flex items-center gap-1.5">
            History
            <NotificationBadge
              count={scheduledAlertCount}
              className="static h-4 min-w-[16px] px-1 text-[9px]"
            />
          </span>
        </TabButton>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {q.isLoading && <Loader2 className="mx-auto mt-6 h-4 w-4 animate-spin" />}
        {q.isError && (
          <p className="pt-6 text-center text-sm text-destructive">
            Couldn’t load scheduled tasks. Try reopening this panel.
          </p>
        )}
        {!q.isLoading && !q.isError && items.length === 0 && (
          <p className="pt-6 text-center text-sm text-muted-foreground">
            {tab === 'active'
              ? 'Nothing scheduled. Ask Fias to run something on a schedule — e.g. “every morning summarize my inbox”.'
              : 'No past tasks yet. Completed and cancelled tasks show up here.'}
          </p>
        )}
        {items.map((a) => (
          <ScheduleCard
            key={a.id}
            a={a}
            tab={tab}
            // Flag the rows that reached a terminal state after the last View —
            // mirrors the badge's "finished since seen" count (completed/failed,
            // NOT user cancels). Cancels never triggered an alert, so they never
            // carry the icon.
            justCompleted={
              tab === 'history' &&
              alertSince != null &&
              (a.status === 'completed' || a.status === 'failed') &&
              a.updatedAt > alertSince
            }
            expanded={expandedId === a.id}
            onToggleExpand={() => setExpandedId(expandedId === a.id ? null : a.id)}
            onPause={() => pause.mutate(a.id)}
            onResume={() => resume.mutate(a.id)}
            onCancel={() => cancel.mutate(a.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Options — temporary chat + admin verbose toggles, then the full memory
// manager (moved in-window from Account Settings so it's managed right here).
// ---------------------------------------------------------------------------

export function FiasAIOptionsPane({
  temporaryChat,
  setTemporaryChat,
  verboseMode,
  setVerboseMode,
  scheduledAlertsEnabled,
  setScheduledAlertsEnabled,
  isAdmin,
  onClose,
}: {
  temporaryChat: boolean;
  setTemporaryChat: (v: boolean) => void;
  verboseMode: boolean;
  setVerboseMode: (v: boolean) => void;
  scheduledAlertsEnabled: boolean;
  setScheduledAlertsEnabled: (v: boolean) => void;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const Row = ({
    label,
    hint,
    checked,
    onChange,
    testId,
  }: {
    label: string;
    hint: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    testId: string;
  }) => (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="fias-ai-options-pane">
      <PaneHeader title="Options" onClose={onClose} />
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
        <div>
          <Row
            label="Temporary chat"
            hint="This conversation neither reads nor updates memory."
            checked={temporaryChat}
            onChange={setTemporaryChat}
            testId="fias-ai-option-temporary"
          />
          <Row
            label="Scheduled task alerts"
            hint="Badge the Fias AI button when a scheduled task finishes."
            checked={scheduledAlertsEnabled}
            onChange={setScheduledAlertsEnabled}
            testId="fias-ai-option-scheduled-alerts"
          />
          {isAdmin && (
            <Row
              label="Verbose (admin)"
              hint="Stream prompts, tools, and full tool inputs/results."
              checked={verboseMode}
              onChange={setVerboseMode}
              testId="fias-ai-option-verbose"
            />
          )}
        </div>
        {/* Full memory manager (master switch + instructions/rules/learned
            CRUD) lives right here now, not behind a link to settings. */}
        <AiMemoryCard />
      </div>
    </div>
  );
}
